import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faX } from "@fortawesome/free-solid-svg-icons";
import { isEmpty } from "lodash";
import { withAppHOC } from "../../hoc";
import {
  StyledContentContainer,
  StyledFiltersContainer,
  StyledFilters,
  StyledFilterContainer,
  StyledSearchButton,
  StyledResetButton,
  StyledStatusPill,
  StyledIconedText,
} from "../../components/styled";
import PageHeader from "../../components/PageHeader/PageHeader";
import { MessageBox, Dropdown, TextInput } from "../../components/FormElements";
import Loader from "../../components/Loader/Loader";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import NoData from "../../components/NoData/NoData";
import { TableBuilder } from "../../components/TableElements";
import Pagination from "../../components/Pagination/Pagination";
import API from "../../api";
import { RESPONSE_STATUSES } from "../../constants";
import { formatCurrency, getSearchParams,formatCurrency4Decimal } from "../../utils";
import FiltersButton from "../../components/FiltersButton/FiltersButton";
import { Image } from "../../components/Image";

const FakeComplaints = () => {
  const [showFilters, setShowFilters] = useState(false);
  const onClickShowFilters = () => {
    setShowFilters((showFilters) => !showFilters);
  };
  const [filters, setFilters] = useState({
    fOperatorsId: "",
    fCirclesId: "",
    fCategoriesId: "",
    fComplaintResolutionStatus: "",
    fRechargeNumber: "",
    fFromDate: "",
    fToDate: "",
  });
  const [appliedFilters, setAppliedFilters] = useState(filters);
  const [paginationData, setPaginationData] = useState({
    pageNumber: getSearchParams("pageNumber") || 1,
    recordsPerPage: getSearchParams("recordsPerPage") || 30,
  });
  const [apiState, setApiState] = useState({
    apiStatus: "",
    apiMessage: "",
  });

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const exportData = async () => {
    try {
      const response = await API.get(
        `/seller/transactions-export?fOperatorsId=${appliedFilters.fOperatorsId}&fCirclesId=${appliedFilters.fCirclesId}&fCategoriesId=${appliedFilters.fCategoriesId}&fRechargeStatus=${appliedFilters.fRechargeStatus}&fRechargeNumber=${appliedFilters.fRechargeNumber}&fFromDate=${appliedFilters.fFromDate}&fToDate=${appliedFilters.fToDate}&isFakeComplaints=Yes`
      );
      setApiState({
        apiStatus: response?.data?.status ?? "",
        apiMessage: response?.data?.message ?? "",
      });
      setTimeout(() => {
        setApiState({
          apiStatus: "",
          apiMessage: "",
        });
      }, 3000);
    } catch (error) {
      throw new Error(error);
    }
  };

  const readData = async () => {
    try {
      const response = await API.get(
        `/seller/transactions?pageNumber=${paginationData.pageNumber}&recordsPerPage=${paginationData.recordsPerPage}&fOperatorsId=${appliedFilters.fOperatorsId}&fCirclesId=${appliedFilters.fCirclesId}&fCategoriesId=${appliedFilters.fCategoriesId}&fRechargeNumber=${appliedFilters.fRechargeNumber}&fFromDate=${appliedFilters.fFromDate}&fToDate=${appliedFilters.fToDate}&fComplaintResolutionStatus=${appliedFilters.fComplaintResolutionStatus}&isFakeComplaints=Yes`
      );
      return response.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery({
    queryKey: ["fake_complaints_transactions", paginationData, appliedFilters],
    queryFn: readData,
  });

  const reloadData = async () => await refetch();

  const readMeta = async () => {
    try {
      const response = await API.get(`/meta`);
      return response?.data?.data;
    } catch (error) {
      throw new Error(error);
    }
  };

  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: readMeta,
  });

  const filterData = async (e) => {
    e.preventDefault();
    setAppliedFilters(filters);
  };

  const clearFilters = async (e) => {
    e.preventDefault();
    setFilters({
      fOperatorsId: "",
      fCirclesId: "",
      fCategoriesId: "",
      fComplaintResolutionStatus: "Pending",
      fRechargeNumber: "",
      fFromDate: "",
      fToDate: "",
    });
    setAppliedFilters({
      fOperatorsId: "",
      fCirclesId: "",
      fCategoriesId: "",
      fComplaintResolutionStatus: "Pending",
      fRechargeNumber: "",
      fFromDate: "",
      fToDate: "",
    });
  };

  const renderPageHeading = () => (
    <PageHeader
      title="Fake Complaints"
      showAddButton={false}
      showReloadData={true}
      reloadData={reloadData}
      showExportButton={true}
      onClickExport={exportData}
    />
  );

  const operatorsOptions = [
    {
      name: "OPERATOR",
      value: "",
    },
  ];
  !isEmpty(meta?.operators) &&
    meta?.operators?.forEach(({ operatorName, id }) =>
      operatorsOptions?.push({ name: operatorName, value: id })
    );

  const circlesOptions = [
    {
      name: "CIRCLE",
      value: "",
    },
  ];
  !isEmpty(meta?.circles) &&
    meta?.circles?.forEach(({ circleName, id }) =>
      circlesOptions?.push({ name: circleName, value: id })
    );

  const categoriesOptions = [
    {
      name: "CATEGORY",
      value: "",
    },
  ];
  !isEmpty(meta?.categories) &&
    meta?.categories?.forEach(({ category, id }) =>
      categoriesOptions?.push({ name: category, value: id })
    );

  const resolutionStatusOptions = [
    {
      name: "RESOLUTION STATUS",
      value: "",
    },
    {
      name: "All",
      value: "All",
    },
    {
      name: "Pending",
      value: "Pending",
    },
    {
      name: "Resolved",
      value: "Resolved",
    },
  ];

  if (isLoading) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <LoadingSpinner />
        </StyledContentContainer>
      </>
    );
  }

  if (error) {
    return (
      <>
        {renderPageHeading()}
        <StyledContentContainer>
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={error.message}
          />
        </StyledContentContainer>
      </>
    );
  }

  return (
    <>
      <Loader isLoading={isRefetching} />
      {renderPageHeading()}
      <FiltersButton onClick={onClickShowFilters} value={showFilters} />
      <StyledContentContainer graybackground="true">
        {apiState?.apiStatus && apiState?.apiMessage && (
          <MessageBox
            status={apiState?.apiStatus}
            message={apiState?.apiMessage}
            style={{ marginBottom: 20 }}
          />
        )}
        <StyledFiltersContainer className={showFilters ? "show" : ""}>
          <StyledFilters>
            <StyledFilterContainer>
              <Dropdown
                value={filters.fOperatorsId}
                onChange={(value) => handleFilterChange("fOperatorsId", value)}
                placeholder="Operator"
                disabled={isLoading}
                options={operatorsOptions}
              />
              <Dropdown
                value={filters.fCirclesId}
                onChange={(value) => handleFilterChange("fCirclesId", value)}
                placeholder="Circle"
                disabled={isLoading}
                options={circlesOptions}
              />
              <Dropdown
                value={filters.fCategoriesId}
                onChange={(value) => handleFilterChange("fCategoriesId", value)}
                placeholder="Category"
                disabled={isLoading}
                options={categoriesOptions}
              />
              <Dropdown
                value={filters.fComplaintResolutionStatus}
                onChange={(value) =>
                  handleFilterChange("fComplaintResolutionStatus", value)
                }
                placeholder="Resolution Status"
                disabled={isLoading}
                options={resolutionStatusOptions}
              />
              <TextInput
                value={filters.fRechargeNumber}
                onChange={(value) =>
                  handleFilterChange("fRechargeNumber", value)
                }
                placeholder="Recharge Number"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fFromDate}
                onChange={(value) => handleFilterChange("fFromDate", value)}
                placeholder="From Date"
                disabled={isLoading}
              />
              <TextInput
                type="date"
                value={filters.fToDate}
                onChange={(value) => handleFilterChange("fToDate", value)}
                placeholder="To Date"
                disabled={isLoading}
              />
            </StyledFilterContainer>
            <StyledSearchButton onClick={filterData} type="button">
              <FontAwesomeIcon icon={faSearch} />
              Search
            </StyledSearchButton>
            <StyledResetButton onClick={clearFilters} type="button">
              <FontAwesomeIcon icon={faX} />
              Clear
            </StyledResetButton>
          </StyledFilters>
        </StyledFiltersContainer>
        {data?.status === RESPONSE_STATUSES.FAILED && (
          <MessageBox
            status={RESPONSE_STATUSES.FAILED}
            message={data?.message}
          />
        )}
        {data?.data?.length === 0 ? (
          <NoData />
        ) : (
          <>
            <TableBuilder
              isLoading={isLoading}
              tableHeadings={[
                {
                  title: "API Name",
                  dataSelector: "apiName",
                  dataType: "string",
                  cellrenderer: (value) => value || "N/A",
                },
                {
                  title: "Operator",
                  dataSelector: "operatorName",
                  dataType: "string",
                  cellrenderer: (value, item) => (
                    <StyledIconedText>
                      <Image
                        source={item.operatorsLogo || "/no-image.png"}
                        alt={value}
                        style={{ width: "50px", height: "50px" }}
                      />
                      {value}
                    </StyledIconedText>
                  ),
                },
                {
                  title: "Recharge Number",
                  dataSelector: "rechargeNumber",
                  dataType: "string",
                },
                {
                  title: "Amount",
                  dataSelector: "rechargeAmount",
                  dataType: "number",
                  textAlign: "center",
                  cellrenderer: (value) => formatCurrency(value),
                },
                {
                  title: "Chargeback Amount",
                  dataSelector: "chargebackAmount",
                  dataType: "number",
                  textAlign: "center",
                  cellrenderer: (value) => formatCurrency4Decimal(value ?? 0),
                },
                {
                  title: "Transaction Status",
                  dataSelector: "sellersStatus",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value) => (
                    <StyledStatusPill
                      className={
                        value === "Success"
                          ? "green"
                          : value === "Failed"
                          ? "red"
                          : "yellow"
                      }
                    >
                      {value}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: "Complaint Status",
                  dataSelector: "complaintResolutionStatus",
                  dataType: "string",
                  textAlign: "center",
                  cellrenderer: (value, item) => (
                    <StyledStatusPill
                      className={
                        value === "Resolved" && item.sellersStatus === "Success"
                          ? "red"
                          : value === "Resolved" &&
                            item.sellersStatus === "Failed"
                          ? "green"
                          : item.complaintReopened === "Yes"
                          ? "blue"
                          : "yellow"
                      }
                    >
                      {value === "Resolved" && item.sellersStatus === "Success"
                        ? "Rejected"
                        : value === "Resolved" &&
                          item.sellersStatus === "Failed"
                        ? "Accepted"
                        : value === "Pending" &&
                          item.complaintReopened === "Yes"
                        ? "Reopened"
                        : "Pending"}
                    </StyledStatusPill>
                  ),
                },
                {
                  title: "Complaint Raised Time",
                  dataSelector: "complaintRaisedTimestamp",
                  dataType: "string",
                  cellrenderer: (value) => (value ? value : "N/A"),
                },
                {
                  title: "Complaint Resolution Time",
                  dataSelector: "complaintResolutionTimestamp",
                  dataType: "string",
                  cellrenderer: (value) => (value ? value : "N/A"),
                },
                {
                  title: "Buyers Remark",
                  dataSelector: "complaintBuyerRemark",
                  dataType: "string",
                  cellrenderer: (value) => value || "N/A",
                },
                {
                  title: "Operators Id",
                  dataSelector: "sellersOperatorsId",
                  dataType: "string",
                  cellrenderer: (value) => value || "N/A",
                },
                {
                  title: "API Transactions Id",
                  dataSelector: "sellersApiTransactionsId",
                  dataType: "string",
                  cellrenderer: (value) => value || "N/A",
                },
                {
                  title: "Transaction Time",
                  dataSelector: "createdOn",
                  dataType: "string",
                },
              ]}
              tableData={data?.data}
            />
            <Pagination
              totalRecords={data?.totalRecords}
              paginationData={paginationData}
              setPaginationData={setPaginationData}
            />
          </>
        )}
      </StyledContentContainer>
    </>
  );
};

export default withAppHOC(FakeComplaints);
